/*
 * pdp-design.js — behaviour for the new PDP content stack (sections/pdp-*.liquid).
 *
 * Loaded as a real asset with <script src defer>, NOT from a {% javascript %}
 * block: this theme does not deliver {% javascript %} to the storefront, which
 * is why the listicle sticky CTA never once ran on three live landers.
 *
 * The mockup drove these with jQuery. jQuery is not in the theme and CLAUDE.md
 * forbids it, so each behaviour is a custom element instead. Everything degrades
 * to a sensible static state if the script never runs.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
   * CTA scroll target.
   *
   * The nine CTAs are authored as href="#MainProduct", but the buy box's real
   * id is section-scoped — MainProduct-template--123__main — so the anchor
   * matches nothing and the click does nothing. The id is only knowable at
   * runtime: these sections cannot see the buy box section's id from Liquid.
   *
   * Rewriting the href (rather than intercepting the click) keeps it a real
   * link: keyboard, middle-click and open-in-new-tab all behave normally, and
   * with no JS it stays the same inert anchor it is today rather than breaking
   * differently.
   */
  function resolveCtaTargets() {
    var buyBox =
      document.querySelector('[id^="MainProduct-"]') ||
      document.querySelector('.pdp-v3-buy-box') ||
      document.querySelector('.product-nw-v2-new-buy-box');
    if (!buyBox) return;

    if (!buyBox.id) buyBox.id = 'MainProduct-pdpd-target';

    Array.from(document.querySelectorAll('a.pdp-cta__btn[href="#MainProduct"]')).forEach(
      function (a) {
        a.setAttribute('href', '#' + buyBox.id);
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolveCtaTargets);
  } else {
    resolveCtaTargets();
  }

  /*
   * <pdp-results> — counts each stat up and grows its bar when scrolled into view.
   *
   * Static fallback: the Liquid renders the final number and sets the bar width
   * inline, so with no JS the section reads correctly, just without the animation.
   * This only takes over once it is safe to animate.
   */
  if (!customElements.get('pdp-results')) {
    customElements.define(
      'pdp-results',
      class PdpResults extends HTMLElement {
        connectedCallback() {
          this.rows = Array.from(this.querySelectorAll('[data-pdp-stat]'));
          if (!this.rows.length) return;

          if (reduceMotion.matches) return; // leave the static end state in place

          this.rows.forEach(function (row) {
            var bar = row.querySelector('[data-pdp-bar]');
            var value = row.querySelector('[data-pdp-value]');
            if (bar) bar.style.width = '0%';
            if (value) value.textContent = value.dataset.startAt || '0';
          });

          if (!('IntersectionObserver' in window)) {
            this.run();
            return;
          }

          var self = this;
          this.observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                self.run();
                self.observer.disconnect();
              });
            },
            { threshold: 0.25 }
          );
          this.observer.observe(this);

          // Already on screen at load? Run now rather than waiting for a
          // callback that may never arrive.
          var box = this.getBoundingClientRect();
          if (box.top < window.innerHeight && box.bottom > 0) {
            this.run();
            this.observer.disconnect();
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        run() {
          this.rows.forEach(function (row) {
            var bar = row.querySelector('[data-pdp-bar]');
            var value = row.querySelector('[data-pdp-value]');
            var end = parseInt(row.dataset.pdpStat, 10);
            if (isNaN(end)) return;

            if (bar) bar.style.width = end + '%';
            if (!value) return;

            // Write the final value FIRST. If requestAnimationFrame never
            // progresses (throttled/background tab), the visitor still sees the
            // real number rather than the count-up's starting value.
            value.textContent = end;

            var start = parseInt(value.dataset.startAt, 10) || 0;
            var duration = 1500;
            var t0 = null;

            function step(now) {
              if (t0 === null) t0 = now;
              var p = Math.min((now - t0) / duration, 1);
              // ease-out, to settle on the number rather than snap to it
              var eased = 1 - Math.pow(1 - p, 3);
              value.textContent = Math.round(start + (end - start) * eased);
              if (p < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
          });
        }
      }
    );
  }

  /*
   * <pdp-timeline> — reveals each row as it comes into view and fills the
   * connecting line behind it.
   *
   * The mockup did this with a jQuery scroll handler that wrote a height onto
   * every row on every scroll event. Here the reveal is an IntersectionObserver
   * (no work while nothing is entering) and the fill is one rAF-throttled pass
   * that writes a 0-1 progress ratio, letting CSS do the sizing.
   *
   * Static fallback: rows render fully visible and the line renders full; this
   * only takes over when it is safe to animate, so no-JS and reduced-motion
   * both get the finished state rather than an empty section.
   */
  if (!customElements.get('pdp-timeline')) {
    customElements.define(
      'pdp-timeline',
      class PdpTimeline extends HTMLElement {
        connectedCallback() {
          this.rows = Array.from(this.querySelectorAll('.pdp-time__row'));
          if (!this.rows.length || reduceMotion.matches) return;

          this.classList.add('is-animated'); // CSS hides the rows only once this is set

          // No IntersectionObserver here. In the design a step lights up when the
          // orange line physically REACHES it (`newHeight >= stepDistances[i]`),
          // not when its row scrolls into view — an observer fires too early and
          // the eggs turn orange well before the line gets to them. The same
          // scroll pass that grows the line therefore also sets the state.
          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }

        /*
         * Throttled on a timestamp rather than requestAnimationFrame. rAF is
         * paused in background/throttled tabs, and the previous version left its
         * `ticking` guard latched true when the frame never arrived — the
         * timeline then froze permanently at whatever state it was in. The work
         * is five getBoundingClientRect reads, so doing it inline is cheap.
         */
        onScroll() {
          var now = Date.now();
          if (now - (this.lastRun || 0) < 16) return;
          this.lastRun = now;
          this.update();
        }

        update() {
          var mid = window.innerHeight * 0.6;
          this.rows.forEach(function (row) {
            var box = row.getBoundingClientRect();
            if (!box.height) return;
            var ratio = (mid - box.top) / box.height;
            var fill = Math.max(0, Math.min(1, ratio));
            row.style.setProperty('--pdpd-fill', fill);
            // The fill starts at this row's marker, so any fill at all means the
            // line has arrived: light the egg and reveal the card.
            row.classList.toggle('is-viewing', fill > 0);
          });
        }
      }
    );
  }

  /*
   * <pdp-accordion> — one open item at a time, keyboard accessible.
   * Built on <details>/<summary> so it works with no JS at all; this only adds
   * the close-the-others behaviour.
   */
  if (!customElements.get('pdp-accordion')) {
    customElements.define(
      'pdp-accordion',
      class PdpAccordion extends HTMLElement {
        connectedCallback() {
          this.addEventListener('toggle', this.onToggle.bind(this), true);
        }

        onToggle(event) {
          var opened = event.target;
          if (!opened.open || opened.tagName !== 'DETAILS') return;
          if (this.dataset.allowMultiple === 'true') return;
          Array.from(this.querySelectorAll('details')).forEach(function (item) {
            if (item !== opened) item.open = false;
          });
        }
      }
    );
  }

  /*
   * <pdp-more> — the design's "Read More Reviews" reveal.
   *
   * The extra cards ship with the hidden attribute, so with no JS they simply
   * stay hidden and the button does nothing visible — no broken half-state.
   * Revealing unsets hidden and removes the button, which is a one-way action
   * in the design too.
   */
  if (!customElements.get('pdp-more')) {
    customElements.define(
      'pdp-more',
      class PdpMore extends HTMLElement {
        connectedCallback() {
          var btn = this.querySelector('[data-pdp-more-btn]');
          if (!btn) return;
          var self = this;
          btn.addEventListener('click', function () {
            var items = self.querySelectorAll('[data-pdp-more-item]');
            items.forEach(function (el) {
              el.removeAttribute('hidden');
              el.removeAttribute('data-pdp-more-item');
            });
            // move focus to the first revealed card so keyboard users are not
            // dropped at the end of the list
            if (items.length) {
              items[0].setAttribute('tabindex', '-1');
              items[0].focus({ preventScroll: true });
            }
            var wrap = btn.closest('.pdp-rev__more-wrap') || btn;
            wrap.remove();
          });
        }
      }
    );
  }

  /*
   * <pdp-video> — click-to-play facade. Holds a poster image until activated,
   * so an autoplaying/preloading embed never costs anything on first paint.
   */
  if (!customElements.get('pdp-video')) {
    customElements.define(
      'pdp-video',
      class PdpVideo extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-pdp-video-play]');
          if (!this.button) return;
          this.button.addEventListener('click', this.activate.bind(this));
        }

        activate() {
          var template = this.querySelector('template');
          if (!template) return;
          var facade = this.querySelector('[data-pdp-video-facade]');
          this.appendChild(template.content.cloneNode(true));
          if (facade) facade.remove();
          var media = this.querySelector('video, iframe');
          if (media && media.tagName === 'VIDEO') media.play();
          if (media) media.focus({ preventScroll: true });
        }
      }
    );
  }
})();
